import PageSkeleton from "@/components/page-skeleton";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  CircularProgress,
  getCoverageScoreClassName,
} from "@/components/ui/circular-progress";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useStreamdownTranslations } from "@/hooks/useStreamdownTranslations";
import { useTranslation } from "@/hooks/useTranslation";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { TeamInstructionCoverageDTO, TeamInstructionDTO } from "@shaarp/common";
import {
  FileTextIcon,
  PencilIcon,
  SaveIcon,
  SparklesIcon,
  XIcon,
} from "lucide-react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Streamdown } from "streamdown";
import {
  TeamInstructionConversation,
  type TeamInstructionConversationHandle,
} from "./team-instruction-conversation";
import { TeamSettingInstructionCoverageDialog } from "./team-setting-instruction-coverage-dialog";

export type TeamSettingInstructionHandle = {
  refetch: () => void;
};

export const TeamSettingInstruction = forwardRef<TeamSettingInstructionHandle>(
  function TeamSettingInstruction(_, ref) {
  const toastError = useToastError();
  const { toast } = useToast();
  const api = useApi();
  const { t } = useTranslation();
  const streamdownTranslations = useStreamdownTranslations();

  const [teamInstruction, setTeamInstruction] =
    useState<TeamInstructionDTO | null>(null);
  const [newInstruction, setNewInstruction] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isEvaluatingScore, setIsEvaluatingScore] = useState<boolean>(false);
  const [showConversation, setShowConversation] = useState<boolean>(false);
  const [showCoverageDialog, setShowCoverageDialog] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editContent, setEditContent] = useState<string>("");
  const [isSavingEdit, setIsSavingEdit] = useState<boolean>(false);
  const conversationRef = useRef<TeamInstructionConversationHandle>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    refetch: fetchData,
  }));

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get<TeamInstructionDTO>(
        `/my/team/instruction`
      );
      setTeamInstruction(data);
      setNewInstruction(data.content || "");
    } catch (error) {
      toastError();
      setTeamInstruction(null);
      setNewInstruction("");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const evaluateCoverage = useCallback(async () => {
    try {
      setIsEvaluatingScore(true);
      await api.post<TeamInstructionCoverageDTO>(
        `/my/team/instruction/coverage`
      );
      const { data } = await api.get<TeamInstructionDTO>(
        `/my/team/instruction`
      );
      setTeamInstruction(data);
    } catch {
      // Score evaluation failed silently — non-blocking
    } finally {
      setIsEvaluatingScore(false);
    }
  }, []);

  const handleConversationSaved = async () => {
    setShowConversation(false);
    await fetchData();
    evaluateCoverage();
  };

  const handleStartEditing = () => {
    setEditContent(newInstruction);
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    setEditContent("");
  };

  const handleSaveEdit = async () => {
    setIsSavingEdit(true);
    try {
      await api.post("/my/team/instruction", { content: editContent });
      toast({
        title: t("team.components.instruction.toast.success.title"),
        description: t("team.components.instruction.toast.success.description"),
      });
      setIsEditing(false);
      await fetchData();
      evaluateCoverage();
    } catch {
      toastError();
    } finally {
      setIsSavingEdit(false);
    }
  };

  return (
    <>
      <Dialog
        open={showConversation}
        onOpenChange={(open) => {
          if (open) {
            setShowConversation(true);
          } else {
            conversationRef.current?.requestClose();
          }
        }}
      >
        <DialogPortal>
          <DialogOverlay className="overflow-y-auto">
            <div className="flex min-h-full items-center justify-center py-8 px-4">
              <DialogPrimitive.Content
                className="relative w-full max-w-3xl border bg-background shadow-lg sm:rounded-lg"
                aria-describedby={undefined}
              >
                <DialogTitle className="sr-only">
                  {t("team.components.instruction.conversation.title")}
                </DialogTitle>
                <TeamInstructionConversation
                  ref={conversationRef}
                  onClose={() => setShowConversation(false)}
                  onSaved={handleConversationSaved}
                  hasExistingInstructions={!!teamInstruction?.content}
                />
              </DialogPrimitive.Content>
            </div>
          </DialogOverlay>
        </DialogPortal>
      </Dialog>
      <TeamSettingInstructionCoverageDialog
        open={showCoverageDialog}
        onOpenChange={setShowCoverageDialog}
        coverageScore={teamInstruction?.coverageScore ?? null}
        coverageReasoning={teamInstruction?.coverageReasoning}
        onStartConversation={() => setShowConversation(true)}
        onForceRecalculate={evaluateCoverage}
        isEvaluatingScore={isEvaluatingScore}
      />
      <Card>
        <CardHeader ref={headerRef} className="scroll-mt-20">
          <div className="flex justify-between items-center gap-4">
            <div>
              <CardTitle>{t("team.components.instruction.title")}</CardTitle>
              <CardDescription>
                {t("team.components.instruction.description")}
              </CardDescription>
            </div>
            <div className="flex items-center gap-4">
              {(isEvaluatingScore ||
                teamInstruction?.coverageScore != null) && (
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => setShowCoverageDialog(true)}
                      disabled={
                        isEvaluatingScore ||
                        teamInstruction?.coverageScore == null
                      }
                      className="cursor-pointer disabled:cursor-default"
                    >
                      <CircularProgress
                        value={teamInstruction?.coverageScore ?? 0}
                        size={45}
                        strokeWidth={5}
                        indeterminate={isEvaluatingScore}
                        strokeClassName={
                          teamInstruction?.coverageScore != null
                            ? getCoverageScoreClassName(
                                teamInstruction.coverageScore
                              )
                            : undefined
                        }
                      />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t(
                      "team.components.instruction.conversation.buttonTooltip"
                    )}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading && <PageSkeleton />}
          {!isLoading &&
            (isEditing ? (
              <Textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="min-h-[300px] font-mono text-sm"
              />
            ) : newInstruction ? (
              <Streamdown
                className="ai-html-content w-full text-sm [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
                translations={streamdownTranslations}
              >
                {newInstruction}
              </Streamdown>
            ) : (
              <Empty className="border">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <FileTextIcon />
                  </EmptyMedia>
                  <EmptyTitle>
                    {t("team.components.instruction.emptyTitle")}
                  </EmptyTitle>
                  <EmptyDescription>
                    {t("team.components.instruction.emptyDescription")}
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            ))}
        </CardContent>
        <CardFooter className="flex justify-between px-6 py-4 border-t">
          <div className="flex items-center gap-2">
            <Button onClick={() => setShowConversation(true)}>
              <SparklesIcon className="mr-2 size-4" />
              {t("team.components.instruction.conversation.aiButton")}
            </Button>
            {isEditing ? (
              <>
                <Button
                  variant="outline"
                  onClick={handleSaveEdit}
                  disabled={isSavingEdit}
                >
                  <SaveIcon className="mr-2 size-4" />
                  {t("team.components.instruction.saveButton")}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCancelEditing}
                  disabled={isSavingEdit}
                >
                  <XIcon className="mr-2 size-4" />
                  {t("team.components.instruction.cancelButton")}
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={handleStartEditing}>
                <PencilIcon className="mr-2 size-4" />
                {t("team.components.instruction.editButton")}
              </Button>
            )}
          </div>
          {teamInstruction?.updatedAt && teamInstruction?.lastUpdatedBy && (
            <div className="text-sm text-gray-400">
              {t("team.components.instruction.lastUpdated", {
                date: new Date(teamInstruction.updatedAt).toLocaleString(),
                user: teamInstruction.lastUpdatedBy.fullName,
              })}
            </div>
          )}
        </CardFooter>
      </Card>
    </>
  );
});
